import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

// Obtener todas las reglas de aprobación de un cliente
export const getAllApprovalRules = async (clientId) => {
  try {
    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/approval-rules`);
    
    // Validar la respuesta y asegurar que siempre devolvamos un array
    const data = response.data;
    
    if (Array.isArray(data)) {
      return data;
    } else if (data && Array.isArray(data.rules)) {
      return data.rules;
    } else if (data && Array.isArray(data.data)) {
      return data.data;
    } else {
      console.warn("API returned unexpected format:", data);
      return [];
    }
  } catch (error) {
    console.error("Error al obtener las reglas de aprobación:", error);
    
    // Si es un error 404, significa que el endpoint no existe aún
    if (error.response?.status === 404) {
      console.info("Approval rules endpoint not implemented yet, returning mock data");
      return getMockApprovalRules();
    }
    
    // Para otros errores de red, también devolver datos mock para testing
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.info("Network error, returning mock data for testing");
      return getMockApprovalRules();
    }
    
    // Para otros errores, devolver array vacío
    console.error("API Error details:", error.response?.data || error.message);
    return [];
  }
};

// Obtener una regla de aprobación específica
export const getApprovalRuleById = async (clientId, ruleId) => {
  try {
    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/approval-rules/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error("Error al obtener la regla de aprobación:", error);
    throw error;
  }
};

// Crear una nueva regla de aprobación
export const createApprovalRule = async (clientId, ruleData) => {
  try {
    const response = await axiosWithAuth.post(`${API_URL}/workflows/p2p/${clientId}/approval-rules`, ruleData);
    return response.data;
  } catch (error) {
    console.error("Error al crear la regla de aprobación:", error);
    throw error;
  }
};

// Actualizar una regla de aprobación existente
export const updateApprovalRule = async (clientId, ruleId, ruleData) => {
  try {
    const response = await axiosWithAuth.put(`${API_URL}/workflows/p2p/${clientId}/approval-rules/${ruleId}`, ruleData);
    return response.data;
  } catch (error) {
    console.error("Error al actualizar la regla de aprobación:", error);
    throw error;
  }
};

// Eliminar una regla de aprobación
export const deleteApprovalRule = async (clientId, ruleId) => {
  try {
    const response = await axiosWithAuth.delete(`${API_URL}/workflows/p2p/${clientId}/approval-rules/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error("Error al eliminar la regla de aprobación:", error);
    throw error;
  }
};

// Evaluar reglas para una orden específica
export const evaluateApprovalRules = async (clientId, orderData) => {
  try {
    const response = await axiosWithAuth.post(`${API_URL}/workflows/p2p/${clientId}/approval-rules/evaluate`, orderData);
    return response.data;
  } catch (error) {
    console.error("Error al evaluar las reglas de aprobación:", error);
    throw error;
  }
};

// Generar regla de negocio con IA
export const generateBusinessRuleWithAI = async (clientId, options = {}) => {
  try {
    // Construir el payload basado en las opciones proporcionadas
    const payload = {};
    
    // Agregar prompt si existe
    if (options.prompt && options.prompt.trim()) {
      payload.prompt = options.prompt.trim();
    }
    
    // Agregar archivos si existen
    if (options.files && Array.isArray(options.files) && options.files.length > 0) {
      payload.files = options.files;
    }
    
    // Validar que al menos uno de los parámetros esté presente
    if (!payload.prompt && !payload.files) {
      throw new Error("Se debe proporcionar al menos un prompt o archivos para generar la regla");
    }
    
    const response = await axiosWithAuth.post(`${API_URL}/ai-business-rules/${clientId}/generate-rule`, payload); 
    
    // Validar la respuesta
    const data = response.data;
    
    if (data && data.success && data.data) {
      return data.data;
    } else {
      console.warn("API returned unexpected format for AI business rule generation:", data);
      return data; // Devolver la respuesta completa si no tiene el formato esperado
    }
  } catch (error) {
    console.error("Error al generar regla de negocio con IA:", error);
    
    // Si es un error 404, significa que el endpoint no existe aún
    if (error.response?.status === 404) {
      console.info("AI business rules endpoint not implemented yet, returning mock data");
      return getMockAIBusinessRule(options.prompt || "Documento cargado");
    }
    
    // Para otros errores de red, también devolver datos mock para testing
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.info("Network error, returning mock data for testing");
      return getMockAIBusinessRule(options.prompt || "Documento cargado");
    }
    
    // Para otros errores, re-lanzar la excepción
    console.error("API Error details:", error.response?.data || error.message);
    throw error;
  }
};

// Validar un request específico con las reglas de negocio de IA
export const validateRequestWithAI = async (clientId, requestId) => {
  try {
    const response = await axiosWithAuth.post(`${API_URL}/ai-business-rules/${clientId}/validate-request/${requestId}`);
    
    // Validar la respuesta
    const data = response.data;
    
    if (data && data.success !== undefined) {
      return data;
    } else {
      console.warn("API returned unexpected format for request validation:", data);
      return data; // Devolver la respuesta completa si no tiene el formato esperado
    }
  } catch (error) {
    console.error("Error al validar el request con IA:", error);
    
    // Si es un error 404, significa que el endpoint no existe aún
    if (error.response?.status === 404) {
      console.info("AI request validation endpoint not implemented yet, returning mock data");
      return getMockRequestValidation(requestId);
    }
    
    // Para otros errores de red, también devolver datos mock para testing
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
      console.info("Network error, returning mock data for testing");
      return getMockRequestValidation(requestId);
    }
    
    // Para otros errores, re-lanzar la excepción
    console.error("API Error details:", error.response?.data || error.message);
    throw error;
  }
};

// Operadores soportados para condiciones
export const APPROVAL_RULE_OPERATORS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'not_equals',
  GREATER_THAN: 'greater_than',
  GREATER_THAN_OR_EQUAL: 'greater_than_or_equal',
  LESS_THAN: 'less_than',
  LESS_THAN_OR_EQUAL: 'less_than_or_equal',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'not_contains',
  STARTS_WITH: 'starts_with',
  ENDS_WITH: 'ends_with',
  IN_ARRAY: 'in_array',
  NOT_IN_ARRAY: 'not_in_array'
};

// Función helper para crear una estructura de regla de ejemplo
export const createRuleTemplate = () => {
  return {
    name: "",
    conditions: [
      {
        field: "",
        operator: "",
        value: "",
        table: "initial_purchase_request"
      }
    ],
    required_approvals: [],
    priority: 1
  };
};

// Datos de ejemplo para testing (remover cuando el backend esté listo)
export const getMockApprovalRules = () => {
  return [
    {
      id: 1,
      name: "High Budget Approval",
      conditions: [
        {
          field: "budget",
          operator: "greater_than",
          value: "10000",
          table: "initial_purchase_request"
        }
      ],
      required_approvals: ["Finance Manager", "General Manager"],
      priority: 1
    },
    {
      id: 2,
      name: "Department Specific Approval",
      conditions: [
        {
          field: "department",
          operator: "equals",
          value: "IT",
          table: "initial_purchase_request"
        }
      ],
      required_approvals: ["IT Manager"],
      priority: 2
    }
  ];
};

// Datos de ejemplo para IA business rules (remover cuando el backend esté listo)
export const getMockAIBusinessRule = (prompt) => {
  return {
    prompt: prompt,
    query: `SELECT CASE WHEN ipr.total_amount > 5000 THEN true ELSE false END as rule_result FROM client_123.initial_purchase_requests ipr WHERE ipr.id = $1`,
    description: `Regla generada a partir de: "${prompt}"`,
    required_approvals: ["Gerente", "Supervisor"],
    clientId: "123"
  };
};

// Datos de ejemplo para validación de request (remover cuando el backend esté listo)
export const getMockRequestValidation = (requestId) => {
  return {
    success: true,
    data: {
      requestId: requestId,
      validationStatus: "approved",
      requiredApprovals: ["Finance Manager", "Department Manager"],
      completedApprovals: ["Department Manager"],
      pendingApprovals: ["Finance Manager"],
      businessRulesApplied: [
        {
          ruleName: "High Budget Approval",
          ruleResult: true,
          description: "Request requires finance approval due to amount > $5000"
        }
      ],
      validationTimestamp: new Date().toISOString()
    }
  };
};
